/* 
* Copyright (c) 2026 Send2Me, media capture and delivery platform. All rights reserved. 
* Distributed under the TruVideo proprietary license. 
*/
import { useState, useRef } from 'react';
import PropTypes from 'prop-types';
import CaptureHeader from '../CaptureHeader';
import ReferenceTooltip from './ReferenceTooltip';
import StepItem from './StepItem';
import './capture.css';
import { DEFAULT_CUSTOMER_REFERENCE_LABEL, resolveCustomerReferenceLabel } from '../../utils/customerReferenceLabel';

// Import custom icons
import goodLightingWhiteIcon from '../../assets/Good lighting-white.svg';
import goodLightingIcon from '../../assets/Good lighting.svg';
import recordMediaWhiteIcon from '../../assets/Record media-white.svg';
import recordMediaIcon from '../../assets/Record media.svg';
import reviewSubmitWhiteIcon from '../../assets/Review & submit-white.svg';
import reviewSubmitIcon from '../../assets/Review & submit.svg';
import secureTransmissionWhiteIcon from '../../assets/Secure transmission-white.svg';
import secureTransmissionIcon from '../../assets/Secure transmission.svg';

const Welcome = ({
  assignment,
  workflow,
  tenant,
  branding,
  pageBgStyle,
  welcomeBgClass,
  onViewInstructions
}) => {
  const currentTheme = branding.theme || 'light';
  const isDark = ['dark', 'synthwave', 'halloween', 'forest', 'black', 'luxury', 'dracula', 'business', 'night', 'coffee', 'dim', 'sunset'].includes(currentTheme);

  const [showTooltip, setShowTooltip] = useState(false);
  const tooltipTriggerRef = useRef(null);
  const dynamicCustomerReferenceLabel = resolveCustomerReferenceLabel(workflow);
  const welcomeTitle = workflow.branding?.welcomeTitle || 'Welcome, {name}'
  const welcomeSubtitle = workflow.branding?.welcomeSubtitle

  return (
    <div
      data-theme={currentTheme}
      className="min-h-[100dvh] flex flex-col font-sans transition-colors duration-500 bg-base-100 text-base-content overflow-x-hidden pt-[env(safe-area-inset-top,20px)] capture-container"
    >
      {/* Top Header Row */}
      <div className="w-full max-w-lg mx-auto">
        <CaptureHeader expiresAt={assignment?.expiresAt} brandLogo={workflow.branding?.logoUrl} branding={branding} type='before-capture' />
      </div>

      <div className="flex-1 px-3 w-full max-w-lg mx-auto flex flex-col pb-[140px]">
        {/* Assignment Info Section */}
        <div className="mb-4">
          <h1 className="text-[20px] font-bold tracking-tight mb-2 text-base-content leading-none custom-line-break">
            {welcomeTitle.replaceAll('{name}', assignment.submitterName || '') || 'Welcome'}
          </h1>
          <div className="flex flex-col gap-1.5 text-[14px]">
            <div className="flex justify-between items-start overflow-visible custom-line-break w-full">
              <span>{dynamicCustomerReferenceLabel === DEFAULT_CUSTOMER_REFERENCE_LABEL ? 'Reference' : dynamicCustomerReferenceLabel}:</span>
              <span className="relative inline-block text-right shrink-0">
                <button
                  type="button"
                  ref={tooltipTriggerRef}
                  onClick={() => setShowTooltip(!showTooltip)}
                  className="text-primary underline decoration-dashed focus:outline-none custom-line-break"
                >
                  {assignment.customerReferenceNumber}
                </button>
                <ReferenceTooltip
                  assignment={assignment}
                  workflow={workflow}
                  show={showTooltip}
                  onClose={() => setShowTooltip(false)}
                  theme={currentTheme}
                  triggerRef={tooltipTriggerRef}
                />
              </span>
            </div>
          </div>
        </div>

        {/* Info Box */}
        <div className="mb-5 bg-primary/20 text-base-content p-4 rounded-xl leading-relaxed text-[15px] border border-primary/5">
          {welcomeSubtitle ? (
            <p className="mb-0 custom-line-break leading-tight">{welcomeSubtitle.replaceAll('{creator_name}', assignment.creatorName)}</p>
          ) : (
            <p className="mb-0 leading-tight">
              Creating a claim can be stressful but we&apos;re going to walk you through every step to help you get things moving along quickly and efficiently. {assignment.creatorName && <><br /> - {assignment.creatorName}</>}
            </p>
          )}
        </div>

        {/* How it works section */}
        <div className="mb-8">
          <h2 className="text-[20px] font-bold tracking-tight text-base-content mb-6">How it works</h2>

          <div className="space-y-7">
            <StepItem
              icon={isDark ? goodLightingWhiteIcon : goodLightingIcon}
              title="Good lighting"
              description="Find a well-lit area. Natural daylight works best"
            />
            <StepItem
              icon={isDark ? recordMediaWhiteIcon : recordMediaIcon}
              title="Record media"
              description="Capture imagery from multiple angles"
            />
            <StepItem
              icon={isDark ? secureTransmissionWhiteIcon : secureTransmissionIcon}
              title="Secure transmission"
              description="Information is encrypted and shared securely"
            />
            <StepItem
              icon={isDark ? reviewSubmitWhiteIcon : reviewSubmitIcon}
              title="Review & submit"
              description="Quick guided capture process"
            />
          </div>
        </div>
      </div>

      {/* Bottom Fixed Section — fixed works reliably on iOS Safari unlike sticky in flex */}
      <div className="fixed bottom-0 left-0 right-0 bg-base-100 px-3 pt-3 z-20"
        style={{ paddingBottom: 'max(env(safe-area-inset-bottom), 16px)' }}
      >
        <div className="w-full max-w-lg mx-auto flex flex-col gap-3">
          <button
            onClick={onViewInstructions}
            className="w-full btn btn-primary font-bold text-[20px] py-2 rounded-xl shadow-xl transition-all active:scale-95 border border-[#00adef]/20"
          >
            Continue
          </button>
        </div>
      </div>
    </div>
  );
};

export default Welcome;

Welcome.propTypes = {
  assignment: PropTypes.shape({
    creatorName: PropTypes.string,
    expiresAt: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    submitterName: PropTypes.string,
    customerReferenceNumber: PropTypes.string,
  }),
  workflow: PropTypes.shape({
    branding: PropTypes.shape({
      welcomeTitle: PropTypes.string,
      welcomeSubtitle: PropTypes.string,
      logoUrl: PropTypes.string,
    }),
  }),
  tenant: PropTypes.any,
  branding: PropTypes.shape({
    theme: PropTypes.string,
  }),
  pageBgStyle: PropTypes.any,
  welcomeBgClass: PropTypes.string,
  onViewInstructions: PropTypes.func,
};
